import React, { useMemo } from 'react';
import CollapsibleSection from '../../../components/form/CollapsibleSection';
import AgentOperationSettings from './AgentOperationSettings';
import LlmProfileSelects from './LlmProfileSelects';
import ProcessingFeedbackFields from './ProcessingFeedbackFields';
import LongRunningAnalysisFields from './LongRunningAnalysisFields';
import Select from '../../../components/ui/Select';
import {
  AGENT_RELEVANCE_MODE_OPTIONS,
  DEFAULT_ESCALATION_COOLDOWN_HOURS,
  MESSAGE_FORMAT_OPTIONS,
  MESSAGE_POLISH_MODE_OPTIONS,
  RESPONSE_DEPTH_OPTIONS,
} from '../constants';

function SettingsGroup({ title, description, children }) {
  return (
    <div className="space-y-4 rounded-lg border border-surface-border bg-slate-50/60 p-4">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
          {title}
        </h3>
        {description && (
          <p className="text-xs text-content-muted mt-0.5 leading-relaxed">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function buildAdvancedSummary({
  llmModelLines,
  llmEfforts,
  llmModelLineId,
  llmEffortId,
  sessionTimeoutHours,
  sessionClosePolicy,
  escalationCooldownHours,
  knowledgeRelevanceMode,
  knowledgeFileButtonEnabled,
  interactiveOptionsEnabled,
  requireRegisteredContact,
  processingFeedback,
  messagePolishMode,
  responseDepth,
  messageFormat,
  longRunningAnalysis,
  shareCrossAgentContext,
  visualOutputEnabled,
  visualOutputStyleInstructions,
  visualOutputAssets,
}) {
  const modelLabel = llmModelLines.find((line) => line.id === llmModelLineId)?.label;
  const effortLabel = llmEfforts.find((effort) => effort.id === llmEffortId)?.label;
  const llmPart =
    modelLabel && effortLabel ? `${modelLabel} · ${effortLabel}` : 'Modelo não selecionado';

  const parts = [];

  if (sessionClosePolicy === 'per_interaction') {
    parts.push('Sessão: uma interação');
  } else if (sessionTimeoutHours && sessionTimeoutHours !== '4') {
    parts.push(`Sessão: ${sessionTimeoutHours}h`);
  }

  if (
    escalationCooldownHours &&
    escalationCooldownHours !== DEFAULT_ESCALATION_COOLDOWN_HOURS
  ) {
    parts.push(`Pausa pós-transbordo: ${escalationCooldownHours}h`);
  }

  if (knowledgeRelevanceMode && knowledgeRelevanceMode !== 'high') {
    const label = AGENT_RELEVANCE_MODE_OPTIONS.find(
      (option) => option.value === knowledgeRelevanceMode
    )?.label;
    if (label) {
      parts.push(`Relevância: ${label.toLowerCase()}`);
    }
  }

  if (!knowledgeFileButtonEnabled) {
    parts.push('Arquivo original desativado');
  }

  if (interactiveOptionsEnabled) {
    parts.push('Opções em lista');
  }

  if (visualOutputEnabled) {
    const visualParts = ['Saída visual ativa'];
    if (visualOutputStyleInstructions?.trim()) {
      visualParts.push('Guia de estilo');
    }
    if (visualOutputAssets?.logo) {
      visualParts.push('Logo configurada');
    }
    if (visualOutputAssets?.reference) {
      visualParts.push('Referência visual');
    }
    parts.push(visualParts.join(' · '));
  }

  if (requireRegisteredContact) {
    parts.push('Somente cadastrados');
  }

  if (shareCrossAgentContext) {
    parts.push('Contexto cross-agent');
  }

  if (!processingFeedback.enabled) {
    parts.push('Feedback desativado');
  } else if (!processingFeedback.typingForText) {
    parts.push('Digitando desativado');
  }

  if (messagePolishMode === 'light') {
    parts.push('Polish: leve');
  } else if (messagePolishMode === 'skip') {
    parts.push('Polish: desativado');
  }
  if (responseDepth === 'expert') {
    parts.push('Profundidade: expert');
  }
  if (messageFormat === 'rich') {
    parts.push('Formatação: rica');
  }
  if (longRunningAnalysis.enabled) {
    parts.push('Análises em segundo plano');
  }

  return parts.length ? `${llmPart} · ${parts.join(' · ')}` : llmPart;
}

function AgentAdvancedSettings({
  llmModelLines,
  llmEfforts,
  llmModelLineId,
  setLlmModelLineId,
  llmEffortId,
  setLlmEffortId,
  isLoadingLlmCatalog,
  llmCatalogError,
  sessionClosePolicy,
  setSessionClosePolicy,
  sessionTimeoutHours,
  setSessionTimeoutHours,
  escalationCooldownHours,
  setEscalationCooldownHours,
  knowledgeRelevanceMode,
  setKnowledgeRelevanceMode,
  knowledgeFileButtonEnabled,
  setKnowledgeFileButtonEnabled,
  interactiveOptionsEnabled,
  setInteractiveOptionsEnabled,
  interactiveListButtonLabel,
  setInteractiveListButtonLabel,
  visualOutputEnabled,
  setVisualOutputEnabled,
  visualOutputFormats,
  setVisualOutputFormats,
  visualOutputStyleInstructions,
  setVisualOutputStyleInstructions,
  visualOutputAssets,
  setVisualOutputAssets,
  agentId = null,
  requireRegisteredContact,
  setRequireRegisteredContact,
  shareCrossAgentContext,
  setShareCrossAgentContext,
  processingFeedback,
  onProcessingFeedbackChange,
  messagePolishMode,
  setMessagePolishMode,
  responseDepth,
  setResponseDepth,
  messageFormat,
  setMessageFormat,
  longRunningAnalysis,
  setLongRunningAnalysis,
}) {
  const summary = useMemo(
    () =>
      buildAdvancedSummary({
        llmModelLines,
        llmEfforts,
        llmModelLineId,
        llmEffortId,
        sessionTimeoutHours,
        sessionClosePolicy,
        escalationCooldownHours,
        knowledgeRelevanceMode,
        knowledgeFileButtonEnabled,
        interactiveOptionsEnabled,
        requireRegisteredContact,
        processingFeedback,
        messagePolishMode,
        responseDepth,
        messageFormat,
        longRunningAnalysis,
        shareCrossAgentContext,
        visualOutputEnabled,
        visualOutputStyleInstructions,
        visualOutputAssets,
      }),
    [
      llmModelLines,
      llmEfforts,
      llmModelLineId,
      llmEffortId,
      sessionTimeoutHours,
      sessionClosePolicy,
      escalationCooldownHours,
      knowledgeRelevanceMode,
      knowledgeFileButtonEnabled,
      interactiveOptionsEnabled,
      requireRegisteredContact,
      processingFeedback,
      messagePolishMode,
      responseDepth,
      messageFormat,
      longRunningAnalysis,
      shareCrossAgentContext,
      visualOutputEnabled,
      visualOutputStyleInstructions,
      visualOutputAssets,
    ]
  );

  return (
    <CollapsibleSection
      title="Configurações avançadas"
      description="Modelo LLM, sessão, knowledge, polish de resposta, acesso e feedback durante processamento."
      summary={summary}
      defaultOpen={false}
    >
      <div className="space-y-4">
        <SettingsGroup
          title="Perfil LLM"
          description="Modelo e esforço usados nas respostas do agente."
        >
          <LlmProfileSelects
            llmModelLines={llmModelLines}
            llmEfforts={llmEfforts}
            llmModelLineId={llmModelLineId}
            setLlmModelLineId={setLlmModelLineId}
            llmEffortId={llmEffortId}
            setLlmEffortId={setLlmEffortId}
            isLoading={isLoadingLlmCatalog}
            catalogError={llmCatalogError}
          />
        </SettingsGroup>

        <SettingsGroup
          title="Sessão e conhecimento"
          description="Modo de encerramento da sessão e rigor da busca nas bases vinculadas."
        >
          <AgentOperationSettings
            sessionClosePolicy={sessionClosePolicy}
            setSessionClosePolicy={setSessionClosePolicy}
            sessionTimeoutHours={sessionTimeoutHours}
            setSessionTimeoutHours={setSessionTimeoutHours}
            escalationCooldownHours={escalationCooldownHours}
            setEscalationCooldownHours={setEscalationCooldownHours}
            knowledgeRelevanceMode={knowledgeRelevanceMode}
            setKnowledgeRelevanceMode={setKnowledgeRelevanceMode}
            knowledgeFileButtonEnabled={knowledgeFileButtonEnabled}
            setKnowledgeFileButtonEnabled={setKnowledgeFileButtonEnabled}
            interactiveOptionsEnabled={interactiveOptionsEnabled}
            setInteractiveOptionsEnabled={setInteractiveOptionsEnabled}
            interactiveListButtonLabel={interactiveListButtonLabel}
            setInteractiveListButtonLabel={setInteractiveListButtonLabel}
            visualOutputEnabled={visualOutputEnabled}
            setVisualOutputEnabled={setVisualOutputEnabled}
            visualOutputFormats={visualOutputFormats}
            setVisualOutputFormats={setVisualOutputFormats}
            visualOutputStyleInstructions={visualOutputStyleInstructions}
            setVisualOutputStyleInstructions={setVisualOutputStyleInstructions}
            visualOutputAssets={visualOutputAssets}
            setVisualOutputAssets={setVisualOutputAssets}
            agentId={agentId}
            disabled={isLoadingLlmCatalog}
            requireRegisteredContact={requireRegisteredContact}
            setRequireRegisteredContact={setRequireRegisteredContact}
            showAccessSettings={false}
          />
        </SettingsGroup>

        <SettingsGroup
          title="Acesso e contatos"
          description="Quem pode conversar com o agente."
        >
          <label className="flex items-center gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={requireRegisteredContact}
              onChange={(e) => setRequireRegisteredContact(e.target.checked)}
            />
            Atender apenas números já cadastrados
          </label>
          <p className="text-xs text-content-muted -mt-2">
            Quando ativo, o agente ignora mensagens de external_id sem contato vinculado. Por
            padrão, novos números são atendidos automaticamente.
          </p>
          <label className="flex items-center gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={shareCrossAgentContext}
              onChange={(e) => setShareCrossAgentContext(e.target.checked)}
            />
            Compartilhar contexto com outros agentes da conta (mesmo contato WhatsApp)
          </label>
          <p className="text-xs text-content-muted -mt-2">
            Quando ativo, o agente recebe os resumos progressivos de outros agentes da mesma conta
            para o mesmo número, sem incluir o histórico bruto de mensagens.
          </p>
        </SettingsGroup>

        <SettingsGroup
          title="Feedback durante processamento"
          description="Mensagens enviadas enquanto o agente processa arquivos ou mídia."
        >
          <ProcessingFeedbackFields
            processingFeedback={processingFeedback}
            onProcessingFeedbackChange={onProcessingFeedbackChange}
          />
        </SettingsGroup>

        <SettingsGroup
          title="Polish de resposta"
          description="Reescreve o rascunho da resposta antes do envio ao usuário."
        >
          <Select
            id="message_polish_mode"
            label="Modo do polish"
            value={messagePolishMode}
            onChange={(e) => setMessagePolishMode(e.target.value)}
            options={MESSAGE_POLISH_MODE_OPTIONS}
            hint="Controla o passo LLM que refina o texto final. Desativado envia o rascunho sem alteração."
          />
          <Select
            id="response_depth"
            label="Profundidade das respostas"
            value={responseDepth}
            onChange={(e) => setResponseDepth(e.target.value)}
            options={RESPONSE_DEPTH_OPTIONS}
            hint="Expert preserva dossiês, métricas, comparações e critérios em pedidos de análise. Saudações e dúvidas simples continuam curtas."
          />
          <Select
            id="message_format"
            label="Formatação das mensagens"
            value={messageFormat}
            onChange={(e) => setMessageFormat(e.target.value)}
            options={MESSAGE_FORMAT_OPTIONS}
            hint="Rica mantém todo o conteúdo e adiciona hierarquia visual no WhatsApp e no histórico do painel."
          />
        </SettingsGroup>

        <SettingsGroup
          title="Análises em segundo plano"
          description="Evita que análises expert longas bloqueiem a fila de mensagens."
        >
          <LongRunningAnalysisFields
            value={longRunningAnalysis}
            onChange={setLongRunningAnalysis}
          />
        </SettingsGroup>
      </div>
    </CollapsibleSection>
  );
}

export default AgentAdvancedSettings;
